/**
 * chartTemplate.ts
 *
 * Pure capture/apply logic for "Save as chart template" (#1050) — reusable,
 * design-only chart presets. A template captures ONLY: chart type, the
 * widget's explicit ChartDesign overrides (axis/gridlines/legend/
 * data-labels/title), and colour overrides. It never captures table
 * selection, rows/columns picks, data type, title TEXT, or any data — same
 * principle as dashboard templates (#971 Phase 2), applied to one widget's
 * design instead of a whole dashboard's structure.
 *
 * Deliberately client-side and generic rather than mirroring
 * TemplateFromDashboard: there is no server-side transform needed because
 * capture is a plain field pick, not a strip of a shape that also carries
 * data (a DashboardConfig does; a chart template payload never did). The
 * server enforces the same thing independently — ChartTemplate has no field
 * to receive job/table_path/selection/spec, so any such field a caller sends
 * is silently dropped by JSON unmarshal (see CreateChartTemplate).
 *
 * Pure — no React, no fetch — so capture/apply are unit-testable without
 * mounting anything. See chartTemplate.test.ts.
 */

import type { ChartDesign, ChartTemplate, TableGraphWidgetData } from '../types/gotypes'

/** The design-shaped fields a "Save as chart template" call sends the server. */
export interface ChartTemplateCapture {
  name: string
  description?: string
  chart_type?: string
  design?: ChartDesign
  color_overrides?: Record<string, string>
}

/**
 * Captures the design-only payload from a chart widget's current state.
 * Reads exactly three fields off `data` (chart_type, design,
 * color_overrides) — everything else on TableGraphWidgetData (table_path,
 * job, view_mode, data_type, selection, range, title, spec, table_design,
 * view_config, sign_off_snapshot) is never referenced, so it can never leak
 * into the captured payload no matter how the widget is configured.
 */
export function captureChartTemplate(
  data: TableGraphWidgetData | undefined,
  name: string,
  description?: string,
): ChartTemplateCapture {
  return {
    name: name.trim(),
    description: description?.trim() || undefined,
    chart_type: data?.chart_type || undefined,
    design: data?.design,
    color_overrides: data?.color_overrides,
  }
}

/** True once a capture has something worth saving — an empty template (no design, no colours) isn't useful. */
export function chartTemplateHasContent(capture: ChartTemplateCapture): boolean {
  return Boolean(capture.design) || Boolean(capture.color_overrides && Object.keys(capture.color_overrides).length > 0)
}

/**
 * True when applying a template would overwrite the widget's own existing
 * design or colour overrides — the caller uses this to decide whether to
 * confirm before applying (#1050: "confirm before overwriting existing
 * overrides").
 */
export function chartTemplateApplyWouldOverwrite(data: TableGraphWidgetData | undefined): boolean {
  return Boolean(data?.design) || Boolean(data?.color_overrides && Object.keys(data.color_overrides).length > 0)
}

/**
 * Applies a stored template onto the current widget as explicit overrides.
 * This is a full replace of `design` and `color_overrides` (and `chart_type`
 * when the template carries one) — not a merge with whatever the widget
 * already had — because "Apply template" means "make the chart look like
 * this", and every applied property is still a per-widget override
 * afterwards: each keeps its own "revert to theme" affordance
 * (isChartDesignPropertyOverridden / revertChartDesignProperty in
 * chartDesign.ts operate on `data.design` exactly the same as if the user
 * had set each property by hand).
 *
 * Everything else on `data` (table_path, job, view_mode, data_type,
 * selection, title, ...) passes through untouched — applying a template
 * never touches data.
 */
export function applyChartTemplateToWidgetData(
  data: TableGraphWidgetData,
  template: Pick<ChartTemplate, 'chart_type' | 'design' | 'color_overrides'>,
): TableGraphWidgetData {
  return {
    ...data,
    chart_type: template.chart_type || data.chart_type,
    design: template.design,
    color_overrides: template.color_overrides,
  }
}
